import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, Search } from '@/components/ui';
import { Briefcase, CheckSquare, Square } from 'lucide-react';
import { cn } from '@/utils/cn';

interface AssignProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableProjects: any[];
  isLoading: boolean;
  onAssign: (projectIds: string[]) => void;
  isMutating: boolean;
}

export function AssignProjectModal({ isOpen, onClose, availableProjects, isLoading, onAssign, isMutating }: AssignProjectModalProps) {
  const [search, setSearch] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setSelectedProjectIds([]);
      setSearch('');
    }
  }, [isOpen]);

  const filteredProjects = availableProjects.filter(p => 
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.code?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleProject = (id: string) => {
    setSelectedProjectIds(prev => 
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const handleAssign = () => {
    if (selectedProjectIds.length > 0) {
      onAssign(selectedProjectIds);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Giao dự án phụ trách" size="md">
      <div className="p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Tìm kiếm dự án..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500/50"
          />
        </div>

        <div className="border border-slate-200 rounded-md h-64 overflow-y-auto bg-slate-50">
          {isLoading ? (
            <div className="p-4 text-center text-slate-400 sm:text-sm text-xs">Đang tải...</div>
          ) : filteredProjects.length === 0 ? (
            <div className="p-4 text-center text-slate-400 sm:text-sm text-xs italic">Không tìm thấy dự án phù hợp.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredProjects.map(project => {
                const isSelected = selectedProjectIds.includes(project.id);
                return (
                  <div 
                    key={project.id} 
                    onClick={() => toggleProject(project.id)}
                    className={cn(
                      "flex items-center gap-3 p-3 cursor-pointer transition-colors hover:bg-white",
                      isSelected ? "bg-blue-50/50" : ""
                    )}
                  >
                    <div className={cn("flex-shrink-0", isSelected ? "text-blue-600" : "text-slate-300")}>
                      {isSelected ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                    </div>
                    <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="sm:text-sm text-xs text-slate-900 font-normal truncate">{project.name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{project.code || 'N/A'}</p>
                    </div>
                    <span className="text-[11px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 shrink-0">
                      {project.status_code || 'active'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedProjectIds.length > 0 && (
          <div className="text-xs text-blue-600">
            Đã chọn {selectedProjectIds.length} dự án
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Hủy</Button>
          <Button onClick={handleAssign} disabled={selectedProjectIds.length === 0 || isMutating} className="bg-blue-600 hover:bg-blue-700 text-white border-none">
            {isMutating ? 'Đang giao...' : 'Giao dự án'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
